import CryptoJS from 'crypto-js';

// Use environment variable for encryption key in production
const SECRET_KEY = import.meta.env.VITE_ENCRYPTION_KEY || 'camp-connect-dev-key-change-in-production';

/**
 * Encrypts data using AES encryption
 * @param {any} data - Data to encrypt
 * @returns {string|null} - Encrypted string or null if error
 */
export const encryptData = (data) => {
  try {
    const jsonString = JSON.stringify(data);
    const encrypted = CryptoJS.AES.encrypt(jsonString, SECRET_KEY).toString();
    return encrypted;
  } catch (error) {
    console.error('Encryption error:', error);
    return null;
  }
};

/**
 * Decrypts data using AES decryption
 * @param {string} encryptedData - Encrypted data to decrypt
 * @returns {any|null} - Decrypted data or null if error
 */
export const decryptData = (encryptedData) => {
  try {
    const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
    const decrypted = bytes.toString(CryptoJS.enc.Utf8);
    return JSON.parse(decrypted);
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
};

/**
 * Safe localStorage wrapper with encryption
 */
export const safeLocalStorage = {
  /**
   * Set item with encryption
   * @param {string} key - Storage key
   * @param {any} data - Data to store
   * @returns {boolean} - Success status
   */
  setItem: (key, data) => {
    try {
      const encrypted = encryptData(data);
      if (encrypted) {
        localStorage.setItem(key, encrypted);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      return false;
    }
  },
  
  /**
   * Get item with decryption
   * @param {string} key - Storage key
   * @returns {any|null} - Decrypted data or null
   */
  getItem: (key) => {
    try {
      const encrypted = localStorage.getItem(key);
      if (encrypted) {
        return decryptData(encrypted);
      }
      return null;
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return null;
    }
  },
  
  /**
   * Remove item from storage
   * @param {string} key - Storage key
   * @returns {boolean} - Success status
   */
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.error('Error removing from localStorage:', error);
      return false;
    }
  },
  
  /**
   * Clear all encrypted data
   * @returns {boolean} - Success status
   */
  clear: () => {
    try {
      localStorage.clear();
      return true;
    } catch (error) {
      console.error('Error clearing localStorage:', error);
      return false;
    }
  }
};

/**
 * Hash data using SHA256
 * @param {string} data - Data to hash
 * @returns {string} - Hashed string
 */
export const hashData = (data) => {
  try {
    return CryptoJS.SHA256(data).toString();
  } catch (error) {
    console.error('Hashing error:', error);
    return null;
  }
};

/**
 * Generate a secure random key
 * @param {number} length - Key length in bytes
 * @returns {string} - Random key
 */
export const generateSecureKey = (length = 32) => {
  try {
    return CryptoJS.lib.WordArray.random(length).toString();
  } catch (error) {
    console.error('Key generation error:', error);
    return null;
  }
};

/**
 * Validate encryption key strength
 * @param {string} key - Key to validate
 * @returns {boolean} - True if key is strong enough
 */
export const validateEncryptionKey = (key) => {
  if (!key || typeof key !== 'string') return false;
  if (key.length < 16) return false;
  if (key === 'camp-connect-dev-key-change-in-production') return false;
  return true;
};

/**
 * Storage keys for encrypted data
 */
export const STORAGE_KEYS = {
  USER_BEHAVIOR: 'campConnect_userBehavior_encrypted',
  SEARCH_HISTORY: 'campConnect_searchHistory_encrypted',
  USER_PREFERENCES: 'campConnect_userPreferences_encrypted',
  ACTIVITY_DATA: 'campConnect_activityData_encrypted',
  SESSION_DATA: 'campConnect_sessionData_encrypted',
  DISMISSED_MATCHES: 'campConnect_dismissedMatches_encrypted'
};
